import { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import axios from "axios";

//import constants
import { LocaleContext } from "../../../../routing/LangRouter";
import BASE_URL from "../../../../constants/baseUrls";
import { TRAVEL_STYLE_QUESTIONS, TRAVEL_FAVORITE_QUESTIONS } from "../../../../constants/travelQuestions";

//import utils
import { navigateToLoginPage } from "../../../../utils/utility";

//import view components
import StyleQuestion from "../style-questions/style-question";
import FavoriteQuestions from "../favorite-questions/favorite-questions";

function Questions({ totalAnswers }) {
    //general variables
    const navigate = useNavigate();
    const location = useLocation();
    const { locale } = useContext(LocaleContext);
    const accessToken = localStorage.getItem("accessToken");

    //questions
    const [questions, setQuestions] = useState({ styleQuestions: TRAVEL_STYLE_QUESTIONS, favoriteQuestions: TRAVEL_FAVORITE_QUESTIONS });
    useEffect(() => {
        setQuestions({ styleQuestions: TRAVEL_STYLE_QUESTIONS, favoriteQuestions: TRAVEL_FAVORITE_QUESTIONS });
    }, []);

    // user answers
    const [userAnswers, setUserAnswers] = useState(null);
    const getUserAnswers = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/account/user/favorites/`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            setDefaultAnswers(response.data);
        } catch (error) {
            if (error.response.status === 401 && location.pathname === `/${locale}/user-panel/user-favorites`) {
                localStorage.clear();
                navigateToLoginPage(navigate, locale, location);
            } else {
                const answers = JSON.parse(sessionStorage.getItem("favorites"));
                setDefaultAnswers(answers);
            }
        }
    };

    useEffect(() => {
        getUserAnswers();
    }, []);

    const setDefaultAnswers = answers => {
        if (!answers) return;
        setUserAnswers(answers);

        //activate questions based on user answers
        const activateQuestion = (questions, key) => {
            return questions.map(question => {
                if (question.possibleAnswers?.[0].groupName === key || question.name === key) {
                    question.active = true;
                }
                return question;
            });
        };

        //iterate over user answers and activate corresponding questions
        Object.keys(answers).forEach(answerKey => {
            if (answers[answerKey] > 0) {
                setQuestions(prevQuestions => ({
                    ...prevQuestions,
                    styleQuestions: activateQuestion(prevQuestions.styleQuestions, answerKey),
                    favoriteQuestions: activateQuestion(prevQuestions.favoriteQuestions, answerKey),
                }));
            }
        });
    };

    //sets the active state of the current question based on the activeIndex
    const handleQuestionActivation = activeIndex => {
        //update the active state of questions in an array
        const updateActiveState = (questionsArray, index) =>
            questionsArray.map(({ active, ...rest }, i) => (i === index ? { active: true, ...rest } : { active, ...rest }));

        //update the active state of style questions
        setQuestions(prevQuestions => ({
            ...prevQuestions,
            styleQuestions: updateActiveState(prevQuestions.styleQuestions, activeIndex),
        }));

        //if the activeIndex is 4, activate all favorite questions and scroll to the box-question element
        if (activeIndex === 4) {
            setQuestions(prevQuestions => ({
                ...prevQuestions,
                favoriteQuestions: prevQuestions.favoriteQuestions.map(({ active, ...rest }) => ({ active: true, ...rest })),
            }));

            //scroll to a specific element on the page
            const scrollToElement = (selector, offset = 90) => {
                const element = document.querySelector(selector);
                if (element) {
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - offset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth",
                    });
                }
            };
            scrollToElement(".favorite-questions");
        }
    };

    //generate answers
    const createAnswerObject = ({ name, value }) => {
        totalAnswers(values => ({ ...values, [name]: value }));
    };

    return (
        <Grid className="questions" container component="form" rowGap={7} justifyContent="space-between">
            <Grid container item xs={12} lg={5.9} rowGap={6} justifyContent="space-between">
                {questions?.styleQuestions?.map((styleQuestion, index) => {
                    return (
                        <StyleQuestion
                            key={styleQuestion.id}
                            activeIndex={index}
                            styleQuestion={styleQuestion}
                            handleQuestionActivation={handleQuestionActivation}
                            generateAnswers={createAnswerObject}
                            userAnswers={userAnswers}
                        />
                    );
                })}
            </Grid>

            <Grid container item xs={12} lg={5.9} rowGap={{ xs: 3, md: 0 }}>
                {questions.favoriteQuestions?.map((favoriteQuestion, index) => {
                    return (
                        <FavoriteQuestions
                            key={favoriteQuestion.id}
                            favoriteQuestion={favoriteQuestion}
                            generateAnswers={createAnswerObject}
                            userAnswers={userAnswers}
                        />
                    );
                })}
            </Grid>
        </Grid>
    );
}

export default Questions;
